// src/views/UserViews/insights/Insights.tsx
//
// Page shell for the Navigator Insights tab. Wires the range selector and the
// InsightsDAO-backed sections together, arranged as six slots with no section headings so
// the compliance trend card reads as the page's visual anchor: (1) Fleet Status KPI row,
// (2) a 2fr/1fr main row - compliance trend + per-feature strip beside Utilization, now a
// four-card stack (protected hours, device utilization, Time muted - the latter moved here
// from its own trailing-row slot per explicit request - and Core vs. Pro Devices; see
// UtilizationSection.tsx's own header comment for all four), (3) the Firmware / Mobile
// version grid, and (4) a trailing row for the
// now-solo device status donut (previously paired with the Safety section's Time muted card;
// SafetySection.tsx had no remaining content once Time muted moved into slot (2), so it was
// deleted).

import { useEffect, useMemo, useState } from 'react';
import { Box, Container } from '@mui/material';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useAllGroupUserStatuses } from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import UserDashboard from '@/views/UserViews/home/user_dashboard/UserDashboard';
import { getAllOrgCompassDevices } from '@/repository/OrgCompassDevicesDAO';
import {
  buildBreachDeviceNameMap,
  buildBreachGroupMemberMap,
} from '@/models/Insights/workZoneBreachUserResolver';
import { useInsightsData } from '@/hooks/insights/useInsightsData';
import { useInsightsOrgCode } from '@/hooks/insights/useInsightsOrgCode';
import { useEarliestDataDate } from '@/hooks/insights/useEarliestDataDate';
import { useFirmwareLookupTable } from '@/hooks/insights/useFirmwareLookupTable';
import { useLiveActiveUsers } from '@/hooks/insights/useLiveActiveUsers';
import {
  getInsightsDateRange,
  validateCustomRange,
  type InsightsDateRange,
  type InsightsRangePreset,
} from '@/models/Insights/insightsDateRange';
import InsightsHeader from './components/InsightsHeader';
import FleetStatusSection from './components/FleetStatusSection';
import UtilizationSection from './components/UtilizationSection';
import ComplianceTrendChart from './components/ComplianceTrendChart';
import DeviceStatusDonut from './components/DeviceStatusDonut';
import FirmwareVersionCurrency from './components/FirmwareVersionCurrency';
import MobileVersionCurrency from './components/MobileVersionCurrency';
import WorkZoneAdaptCard from './components/WorkZoneAdaptCard';

const DEFAULT_RANGE_PRESET: InsightsRangePreset = '7days';

export default function Insights() {
  // `groups`/`pvdConfigurations` are pulled here only so `<UserDashboard>` below (mounted on
  // this page so a Firmware dialog MAC click has somewhere to render - see
  // FirmwareDetailsDialog.tsx's own header comment) can be given the same 4 props
  // DeviceManagement.tsx already passes it; this page's own sections don't otherwise need
  // either value.
  const { organization, groups, groupsStatus, pvdConfigurations, getUsersGroup } =
    useOrganizationUpdaterProvider();
  const allGroupUserStatuses = useAllGroupUserStatuses();
  const orgCode = useInsightsOrgCode();

  // WorkZoneAdaptCard's User column, primary NAME source (added 2026-09-15, M4 user-
  // attribution feature). The device map remains first in the lookup order because the
  // provider's derived groups state has historically omitted Default for SAFEGDEV.
  // Fetched here, once, following FirmwareDetailsDialog.tsx's own established pattern for
  // this exact same DAO call (that dialog fetches it independently, on its own mount - this
  // adds a second, separate call to the same endpoint rather than deduplicating, which is
  // accepted scope per the plan). On rejection: log and leave the map empty - this state is
  // NEVER wired into WorkZoneAdaptCard's own `breaches` loading/error props, so a rejected
  // fetch here degrades every row to unresolved names without touching
  // work-zone-adapt-loading/work-zone-adapt-error.
  const [workZoneBreachDeviceNameMap, setWorkZoneBreachDeviceNameMap] = useState<
    Map<string, string | null>
  >(new Map());

  useEffect(() => {
    let cancelled = false;

    getAllOrgCompassDevices().then(
      (devices) => {
        if (cancelled) return;
        setWorkZoneBreachDeviceNameMap(buildBreachDeviceNameMap(devices));
      },
      (fetchError: unknown) => {
        // Enrichment is optional: log and leave the map at its current (initially empty)
        // value rather than touching this page's own error state. Mirrors
        // FirmwareDetailsDialog.tsx's own graceful-degradation comment for this same call.
        console.error(
          'Insights: getAllOrgCompassDevices failed (Work Zone Breach User column)',
          fetchError,
        );
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // Roster fallback for device-list misses. `loadGroups()` already fetched each group's
  // members into `group_value`; this memo only indexes that in-memory payload and never
  // triggers another GetGroupMembers call. First-write-wins is documented by the builder.
  const workZoneBreachGroupMemberMap = useMemo(
    () => buildBreachGroupMemberMap(groups),
    [groups],
  );

  const [preset, setPreset] = useState<InsightsRangePreset>(DEFAULT_RANGE_PRESET);
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);

  // Part B: lazy, cached discovery of the org's earliest data date, to bound the Custom
  // picker. `ensureLoaded` is itself a no-op once a fetch has started/finished for this
  // orgCode, so this effect firing again on every render while Custom stays selected costs
  // nothing extra - only the transition into 'custom' (or orgCode finally resolving while
  // already on 'custom') actually triggers a fetch.
  const { state: earliestDataDateState, ensureLoaded: ensureEarliestDataDateLoaded } =
    useEarliestDataDate(orgCode || null);

  useEffect(() => {
    if (preset === 'custom') ensureEarliestDataDateLoaded();
  }, [preset, ensureEarliestDataDateLoaded]);

  const earliestAvailable =
    earliestDataDateState.status === 'ready' ? earliestDataDateState.earliestDate : null;

  // 'custom' ranges come from user input via the header's pickers, not the clock - validate
  // them instead of asking getInsightsDateRange to resolve them (it throws for 'custom').
  // Kept separate from `range` below because InsightsHeader also needs it, to render an
  // inline message when the picked bounds are incomplete or invalid.
  const customRangeValidation = useMemo(
    () => validateCustomRange(customFrom, customTo, undefined, earliestAvailable),
    [customFrom, customTo, earliestAvailable],
  );

  const { from, to } =
    preset === 'custom'
      ? customRangeValidation.ok
        ? customRangeValidation.range
        : { from: null, to: null }
      : getInsightsDateRange(preset);

  // Memoized against the primitive from/to strings, not `customRangeValidation` or the
  // preset - getInsightsDateRange reads the real clock, and a custom range is built from
  // React state, so either path would otherwise produce a new {from, to} object identity on
  // every render and defeat useInsightsData's effect dependency, causing a refetch loop. A
  // `null` range (an incomplete/invalid custom selection) tells useInsightsData not to fire
  // any query yet - reporting_coverage_ratio would return a real SQL NULL if it did.
  const range = useMemo<InsightsDateRange | null>(
    () => (from && to ? { from, to } : null),
    [from, to],
  );

  const {
    fleetStatus,
    deviceStatusBreakdown,
    complianceTrend,
    versionDistribution,
    featureCompliance,
    protectedHours,
    utilization,
    coreProDeviceCounts,
    workZoneBreaches,
  } = useInsightsData(range);

  // Not `orgCode` above: SignalR's group subscriptions are for the signed-in user's own
  // organization, and `useInsightsOrgCode`'s dev override can point at a Cube-only fixture
  // org that the UserStatus API does not know.
  const liveActiveUsers = useLiveActiveUsers(organization?.org_code ?? null);

  // Range-independent: the release catalog has no per-org/per-range parameter, so this is
  // fetched once on mount rather than folded into useInsightsData's range-keyed effect.
  const firmwareLookupTable = useFirmwareLookupTable();
  const catalogUnavailable = firmwareLookupTable.loading || firmwareLookupTable.error !== null;

  const mobileBuckets = versionDistribution.data?.mobile ?? [];

  // Never derive currency data from the catalog while it is loading or failed - the firmware
  // card must fall back to its monochrome distribution-only view, and the mobile card must
  // render no currency bars at all, rather than show stale/zeroed values (Change 6/7).
  const firmwareLatestVersion = catalogUnavailable
    ? null
    : firmwareLookupTable.data?.latest_firmware_version ?? null;
  const mobileLatestByPlatform = catalogUnavailable
    ? new Map<string, string | null>()
    : new Map<string, string | null>([
        ['iOS', firmwareLookupTable.data?.latest_app_version_ios ?? null],
        ['Android', firmwareLookupTable.data?.latest_app_version_android ?? null],
      ]);
  const mobileMinimumByPlatform = catalogUnavailable
    ? new Map<string, string | null>()
    : new Map<string, string | null>([
        ['iOS', firmwareLookupTable.data?.minimum_app_version_ios ?? null],
        ['Android', firmwareLookupTable.data?.minimum_app_version_android ?? null],
      ]);

  return (
    <Box sx={{ bgcolor: '#F4F5F7', minHeight: '100%' }}>
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'grey.200' }}>
        <Container maxWidth="xl" sx={{ py: 2 }}>
          <InsightsHeader
            organizationName={organization?.company_name ?? null}
            selectedPreset={preset}
            onPresetChange={setPreset}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
            customRangeValidation={customRangeValidation}
            earliestAvailable={earliestAvailable}
          />
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FleetStatusSection status={fleetStatus} liveActiveUsers={liveActiveUsers} />

          {/* The page's dominant row: UtilizationSection's returned column is stretched
              (`alignItems: 'stretch'`, the grid default, spelled out explicitly here) to
              match the taller compliance card's row height. ComplianceTrendChart's own
              SectionCard opts itself out of that stretch via `autoHeight`/`alignSelf:
              'start'` whenever its feature-compliance strip is present (always true at this
              call site - see SectionCard.tsx), so only the right column actually stretches.

              UtilizationSection wraps each of its 4 cards in a height-less "slot" `Box`
              rather than letting each `Card`'s own `height: '100%'` be a direct flex item of
              the stretched column - a flex item's `auto` flex-basis inherits its own
              main-size property, so N cards each setting `height: 100%` as direct children
              would each separately claim the full column height and get flex-shrunk into an
              arbitrary N-way split instead of their natural content size (confirmed via
              InsightsMainRowAlignment.test.tsx). The height-less slot has no main-size
              property to inherit from, so it resolves to each card's own natural height.

              Any leftover stretched height (when the 4-card stack is naturally shorter than
              the compliance card) is absorbed by growing card 4 ("Core vs. Pro Devices")
              itself (`flexGrow: 1` on its own slot) rather than an invisible trailing
              spacer, so the last *visible* card's own border reaches the compliance card's
              bottom instead of leaving a gap filled with plain page background behind an
              invisible filler. See UtilizationSection.tsx's own header comment ("Column-height
              tradeoff") for why card 4 specifically was chosen to grow. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <ComplianceTrendChart
              trend={complianceTrend}
              range={range}
              onViewSevenDays={() => setPreset('7days')}
              featureCompliance={featureCompliance}
            />
            <UtilizationSection
              protectedHours={protectedHours}
              utilization={utilization}
              complianceTrend={complianceTrend}
              totalReportingDevices={fleetStatus.data?.totalReportingDevices ?? null}
              coreProDeviceCounts={coreProDeviceCounts}
            />
          </Box>

          <WorkZoneAdaptCard
            breaches={workZoneBreaches}
            deviceNameMap={workZoneBreachDeviceNameMap}
            groupMemberMap={workZoneBreachGroupMemberMap}
            groupsStatus={groupsStatus}
            organization={organization}
            getUsersGroup={getUsersGroup}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <FirmwareVersionCurrency
              buckets={versionDistribution.data?.firmware ?? []}
              loading={versionDistribution.loading}
              error={versionDistribution.error}
              latestVersion={firmwareLatestVersion}
              orgCode={orgCode}
            />
            <MobileVersionCurrency
              buckets={mobileBuckets}
              loading={versionDistribution.loading}
              error={versionDistribution.error}
              latestByPlatform={mobileLatestByPlatform}
              minimumByPlatform={mobileMinimumByPlatform}
            />
          </Box>

          {/* Trailing row: now a single item (Time muted moved into UtilizationSection above
              - see that file's own header comment), so the four-track/2-track-span
              scaffolding this grid used to need - specifically to align the old Time muted
              card's width with the Fleet Status cards at the top of the page - no longer
              applies. Simplified to a plain two-column grid with the donut in the first cell
              and the second cell left empty: this keeps the donut at close to its previous
              rendered width (it used to span 2 of 4 tracks, i.e. ~50% of the row) rather than
              stretching it across the full container, which would look worse for a
              donut-plus-legend than its current proportions. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <DeviceStatusDonut status={deviceStatusBreakdown} />
          </Box>
        </Box>
      </Container>

      {/* Mounted here (not inside any of the range/preset-driven conditional content above)
          so a device card opened from the Firmware dialog's MAC click (see
          FirmwareDetailsDialog.tsx) can never be unmounted by an unrelated re-render of this
          page's own sections - `UserDashboard` renders nothing on its own while closed (see
          its `isDashboardOpen` gate), so its exact position here doesn't otherwise matter.
          Same 4 props `DeviceManagement.tsx` passes it, sourced the same way (Task: Insights
          Firmware Details -> device card). Gated on `organization` only because
          `UserDashboardProps.organization` is non-nullable - this page tolerates a null
          `organization` everywhere else (see `InsightsHeader`'s `organizationName` prop
          above), so this gate is not a loading-state toggle that could unmount an
          already-open card once the page has organization data at all. */}
      {organization && (
        <UserDashboard
          organization={organization}
          groups={groups}
          allGroupUserStatuses={allGroupUserStatuses}
          pvdConfigurations={pvdConfigurations ?? undefined}
        />
      )}
    </Box>
  );
}
